import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { translate } from '@airasia-common/libraries/i18n/LocaleProvider';
import { string, func, bool, object } from 'prop-types';
import {
  ButtonView,
  TextView,
  pd,
  PhoenixThemeProvider,
} from '@airasia-phoenix/web';
import Wrapper from '../Wrapper';
import { ArrowDownIcon } from '../../utils/statics';
import { TripSummary, OneLineTripSummary } from './TripSummary';

const color = pd.sys.colour;
const font = pd.sys.typescale;

const Row = styled.div`
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  align-items: ${({ alignItems }) => alignItems || 'stretch'};
  justify-content: ${({ justifyContent }) => justifyContent || 'flex-start'};
  border-bottom: none;
  gap: ${({ gap }) => gap || '0px'};
`;

const FlightWrapper = styled.div`
  border: ${({ isCollapsed }) =>
    isCollapsed
      ? `1px solid ${color.container_max.color()}`
      : `1px solid ${color.outline_low.color()}`};
  border-radius: 16px;
  background-color: white;
  padding-top: 8px;
  padding-bottom: 8px;
`;

const SemiDivider = styled.div`
  height: 1px;
  background-color: ${color.outline_low.color()};
  width: 80%;
  margin-left: 16px;
  margin-top: 10px;
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${color.outline_low.color()};
  width: 100%;
  margin: ${props => props.marginY || '7px 0'};
`;

const ViewDetail = styled(TextView)`
  display: flex;
  align-items: center;
  margin-right: 170px;
  margin-top: 6px;
  margin-bottom: 4px;
  cursor: pointer;
  justify-content: flex-end;
`;

const Heading = styled(TextView)`
  margin-left: 13px;
  padding: 2px;
`;

const StyledButton = styled(Wrapper)`
  margin-left: 35px;
  padding-top: 30px;
`;

const ArrowDown = styled.img`
  margin-left: 6px;
  height: 4.47px;
  width: 7.54px;
`;

const DownIcon = styled.div`
  height: 6.71px;
  width: 11.31px;
  mask: url(${ArrowDownIcon}) no-repeat center;
  -webkit-mask: url(${ArrowDownIcon}) no-repeat center;
  background-color: ${color.on_interactive.color()};
  margin-right: 20px;
  cursor: pointer;
  transition: transform 0.3s ease;
  transform-origin: center;
  transform: ${({ isCollapsed }) =>
    !isCollapsed ? 'rotate(360deg)' : 'rotate(270deg)'};
`;

const CollapsibleBody = styled.div`
  overflow: hidden;
  transition: max-height 0.3s ease;
  max-height: ${({ isCollapsed }) => (isCollapsed ? '0px' : '1000px')};
`;

const FlightSummaryCard = ({
  origin,
  destination,
  fromDate,
  toDate,
  departureFlight,
  arrivalFlight,
  onChangeFlight,
  isSnapOMSFlightsWeb,
  onClickDownArrow,
  handleToolTipIconClick,
  airlineClassNameDepart,
  airlineClassNameReturn,
  airlineNameDepart,
  airlineNameReturn,
  carrierLogo,
  carrierLogo1,
  carrierLogo2,
  returnCarrierLogo,
  returnCarrierLogo1,
  returnCarrierLogo2,
  handleViewDetails,
  departLabel,
  returnLabel,
  isSticky,
  isRoomDetail,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hasCollapsed, setHasCollapsed] = useState(false);
  const collapsibleRef = useRef(null);
  const imageSrcDown = ArrowDownIcon;
  const ViewDetailText = translate('snapBookingConfirmation.viewDetails');
  const HeadingText = translate('common.selectedStickyFlight.title');

  const handleToggle = () => {
    setIsCollapsed(prev => !prev);
    if (onClickDownArrow) {
      onClickDownArrow();
    }
  };

  useEffect(() => {
    if (isSticky || isRoomDetail) {
      setIsCollapsed(true);
    } else {
      setIsCollapsed(false);
    }
  }, [isSticky, isRoomDetail]);

  useEffect(() => {
    const node = collapsibleRef.current;
    if (!node) return;
    const handleTransitionEnd = () => {
      // After transition ends, if card is collapsed, show OneLineTripSummary
      // If expanded, hide it again.
      setHasCollapsed(isCollapsed);
    };
    node.addEventListener('transitionend', handleTransitionEnd);
    return () => {
      node.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, [isCollapsed]);

  return (
    <PhoenixThemeProvider>
      <FlightWrapper
        isCollapsed={Boolean(isCollapsed && hasCollapsed)}
        id="flight-details"
      >
        <Row justifyContent="space-between" alignItems="center">
          <Row justifyContent="center" alignItems="center" gap="10px">
            <Heading
              font={pd.sys.typescale.body_text_medium}
              color={color.content_primary.color()}
              variant={'span'}
            >
              {HeadingText}
            </Heading>
            {isCollapsed && hasCollapsed && (
              <OneLineTripSummary
                departLabel={departLabel}
                returnLabel={returnLabel}
                departure={departureFlight}
                arrival={arrivalFlight}
                carrierLogo={carrierLogo}
                carrierLogo1={carrierLogo1}
                carrierLogo2={carrierLogo2}
                returnCarrierLogo={returnCarrierLogo}
                returnCarrierLogo1={returnCarrierLogo1}
                returnCarrierLogo2={returnCarrierLogo2}
              />
            )}
          </Row>
          <DownIcon
            className={'arrow-down-icon'}
            onClick={handleToggle}
            isCollapsed={isCollapsed}
          />
        </Row>
        <CollapsibleBody isCollapsed={isCollapsed} ref={collapsibleRef}>
          <Divider marginY={0} />
          <Row>
            <Wrapper
              id="departure-flight"
              padding={['8px 0', '0 0px']}
              width={['355px']}
            >
              <TripSummary
                className="depart"
                origin={origin}
                destination={destination}
                departDate={fromDate}
                {...departureFlight}
                isShowStopsInfo={true}
                travel={true}
                isSnapOMSFlightsWeb={isSnapOMSFlightsWeb}
                airlineCode={departureFlight.airlineCode}
                tripType={`${translate('tripSummary.depart')}:`}
                handleIconClick={handleToolTipIconClick}
                airlineName={airlineNameDepart}
                airlineClassName={airlineClassNameDepart}
                carrierLogo={carrierLogo}
                carrierLogo1={carrierLogo1}
                carrierLogo2={carrierLogo2}
              />
            </Wrapper>
            <Wrapper
              id="return-flight"
              padding={['8px 0', '0 0px']}
              width={['345px']}
              marginLeft={15}
            >
              <TripSummary
                className="return"
                origin={destination}
                destination={origin}
                departDate={toDate}
                {...arrivalFlight}
                isShowStopsInfo={true}
                isSnapOMSFlightsWeb={isSnapOMSFlightsWeb}
                travel={false}
                airlineCode={arrivalFlight.airlineCode}
                tripType={`${translate('tripSummary.return')}:`}
                handleIconClick={handleToolTipIconClick}
                airlineName={airlineNameReturn}
                airlineClassName={airlineClassNameReturn}
                carrierLogo={returnCarrierLogo}
                carrierLogo1={returnCarrierLogo1}
                carrierLogo2={returnCarrierLogo2}
              />
            </Wrapper>
            <Wrapper paddingRight={'6px'}>
              {onChangeFlight && (
                <StyledButton className={'change-flight'}>
                  <ButtonView
                    variant="secondary"
                    label={translate('common.changeFlight')}
                    onClick={onChangeFlight}
                    className={'change-flight-button'}
                  />
                </StyledButton>
              )}
            </Wrapper>
          </Row>
          <SemiDivider />
          <ViewDetail
            className={'view-details'}
            onClick={handleViewDetails}
            font={font.caption_text}
            color={color.content_secondary.color()}
            variant={'span'}
          >
            {ViewDetailText}
            <ArrowDown src={imageSrcDown} alt="Down Arrow Icon" />
          </ViewDetail>
        </CollapsibleBody>
      </FlightWrapper>
    </PhoenixThemeProvider>
  );
};

FlightSummaryCard.propTypes = {
  origin: string.isRequired,
  destination: string.isRequired,
  fromDate: string.isRequired,
  toDate: string.isRequired,
  departureFlight: object.isRequired,
  arrivalFlight: object.isRequired,
  onChangeFlight: func,
  isSnapOMSFlightsWeb: bool,
  onClickDownArrow: func,
  handleToolTipIconClick: func,
  airlineName: string,
  airlineClassName: string,
  carrierLogo: string,
  carrierLogo1: string,
  carrierLogo2: string,
  returnCarrierLogo: string,
  returnCarrierLogo1: string,
  returnCarrierLogo2: string,
  handleViewDetails: func,
  departLabel: string,
  returnLabel: string,
  airlineClassNameDepart: string,
  airlineClassNameReturn: string,
  airlineNameDepart: string,
  airlineNameReturn: string,
  isSticky: bool,
  isRoomDetail: bool,
};

FlightSummaryCard.defaultProps = {
  onChangeFlight: undefined,
  isSnapOMSFlightsWeb: false,
  onClickDownArrow: null,
  handleToolTipIconClick: null,
  airlineName: '',
  airlineClassName: '',
  carrierLogo: '',
  carrierLogo1: '',
  carrierLogo2: '',
  returnCarrierLogo: '',
  returnCarrierLogo1: '',
  returnCarrierLogo2: '',
  handleViewDetails: null,
  departLabel: 'Depart',
  returnLabel: 'Return  ',
  airlineClassNameDepart: '',
  airlineClassNameReturn: '',
  airlineNameDepart: '',
  airlineNameReturn: '',
  isSticky: false,
  isRoomDetail: false,
};

export default FlightSummaryCard;
